/* eslint-disable */
import React, { useEffect, useState } from 'react';
import {
  Calendar, Users, UserCog, Mail,
  FileText, CreditCard, Info, RefreshCw,
} from 'lucide-react';
import Page from '../../../components/Page';
import adminServices from '../../../services/adminServices';


function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function getDaysRemaining(endDate) {
  if (!endDate) return null;
  const end = new Date(endDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}


function ContractStatusBadge({ days }) {
  if (days < 0) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
        Expired
      </span>
    );
  }
  if (days <= 30) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
        Expiring Soon
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-50 text-green-700 border border-green-200">
      Active
    </span>
  );
}
function TrialEndBadge({ trialEndDate }) {
  if (!trialEndDate) return null;

  const days = getDaysRemaining(trialEndDate);

  if (days < 0) return null; 

  if (days === 0) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200">
        Trial ends today
      </span>
    );
  }

  if (days <= 7) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
        Trial ends in {days}d
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
      Trial · {days} days left
    </span>
  );
}

// ─── Usage Bar ────────────────────────────────────────────────────────────────

function UsageBar({ used, total, label, icon: Icon }) {
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;
  const isOver = used > total;
  const fillColor =
    pct >= 100 ? 'bg-red-500' : pct >= 90 ? 'bg-amber-400' : 'bg-violet-600';

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-gray-400" />
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        <span className={`text-sm font-semibold ${isOver ? 'text-red-600' : 'text-gray-700'}`}>
          {used}{' '}
          <span className="font-normal text-gray-400">of {total}</span>
        </span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${fillColor}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      {isOver && (
        <p className="text-xs text-red-600 mt-1.5">
          Over seat limit — contact your account manager to increase seats.
        </p>
      )}
      {!isOver && pct >= 90 && (
        <p className="text-xs text-amber-700 mt-1.5">
          Approaching seat limit ({Math.round(pct)}% used).
        </p>
      )}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function Skeleton({ height = 20, width = '100%' }) {
  return (
    <div
      className="animate-pulse bg-gray-200 rounded-md"
      style={{ height, width }}
    />
  );
}

// ─── Info Tile ────────────────────────────────────────────────────────────────

function InfoTile({ icon: Icon, label, value, valueClass = '' }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} className="text-gray-400" />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <p className={`text-sm font-semibold text-gray-900 ${valueClass}`}>{value}</p>
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

function Card({ children, className = '' }) {
  return (
    <div className={`bg-white border border-gray-200 rounded-xl px-6 py-5 ${className}`}>
      {children}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BillingPage() {
  const [contract, setContract]           = useState(null);
  const [tenantMode, setTenantMode]       = useState(null);
  const [activeLearners, setActiveLearners] = useState(0);
  const [activeCCs, setActiveCCs]         = useState(0);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  useEffect(() => { loadBillingData(); }, []);

  const loadBillingData = async () => {
    setLoading(true);
    setError(null);
    try {
      const contractRes = await adminServices.getBillingContract();
      if (contractRes.ok) {
        setContract(contractRes.data.contract ?? null);
        setTenantMode(contractRes.data.tenantMode ?? null);
        setActiveLearners(contractRes.data.activeLearners ?? 0);
        setActiveCCs(contractRes.data.activeCCs ?? 0);
      } else {
        setError('Failed to load contract details.');
      }
      const paymentRes = await adminServices.getBillingPaymentHistory();
      if (paymentRes.ok) setPaymentHistory(paymentRes.data ?? []);
    } catch (err) {
      console.error('BillingPage error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFullCreator = tenantMode === 'FULL_CREATOR';
  const days = contract ? getDaysRemaining(contract.end_date) : null;

  // ─── Loading ─────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Page title="Billing & Contract">
        <div className="p-8  flex flex-col gap-5">
          <div className="flex flex-col gap-1">
            <Skeleton height={28} width={200} />
            <Skeleton height={14} width={300} />
          </div>
          <Skeleton height={180} />
          <Skeleton height={130} />
          <Skeleton height={220} />
        </div>
      </Page>
    );
  }


  if (error) {
    return (
      <Page title="Billing & Contract">
        <div className="p-8 max-w-4xl">
          <Card className="flex flex-col items-center justify-center py-12 text-center gap-3">
            <Info size={32} className="text-gray-300" />
            <p className="text-sm font-medium text-gray-500">{error}</p>
            <button
              onClick={loadBillingData}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-50 transition-colors"
            >
              <RefreshCw size={14} /> Try Again
            </button>
          </Card>
        </div>
      </Page>
    );
  }


  return (
    <Page title="Billing & Contract">
      <div className="p-8 bg-gray-50 min-h-screen">

        {/* Breadcrumb */}
        <div className="text-sm mb-4 flex items-center gap-2 text-gray-500">
          <span className="text-gray-400">Client Admin</span>
          <span className="text-gray-300">›</span>
          <span className="text-gray-900 font-medium">Billing &amp; Contract</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Billing &amp; Contract</h1>
            <p className="text-sm text-gray-500">Your current contract details with keySkillset</p>
          </div>
          <button
            onClick={loadBillingData}
            className="flex items-center gap-2 text-sm text-gray-600 border border-gray-300 bg-white px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div className="flex flex-col gap-5 ">

          {!contract ? (

            <Card className="flex flex-col items-center justify-center py-12 text-center gap-2 mx-auto max-w-md w-full">
              <FileText size={32} className="text-gray-300 mb-1" />
              <p className="text-sm font-medium text-gray-500">No contract on file</p>
              <p className="text-xs text-gray-400">
                Contact your keySkillset account manager to set up your contract.
              </p>
              <a
                href="mailto:contact@keyskillset.com"
                className="inline-flex items-center gap-2 mt-2 px-4 py-2 text-sm font-medium text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-50 transition-colors"
              >
                <Mail size={14} /> Contact Support
              </a>
            </Card>

          ) : (
            <>

              <Card>
                <div className="flex items-start justify-between mb-5">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Contract Overview</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatDate(contract.start_date)} – {formatDate(contract.end_date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
  { <TrialEndBadge trialEndDate={contract.trial_end_date} />}
  {days !== null && <ContractStatusBadge days={days} />}
</div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <InfoTile icon={Calendar} label="Start Date"    value={formatDate(contract.start_date)} />
                  <InfoTile icon={Calendar} label="End Date"      value={formatDate(contract.end_date)} />
                  <InfoTile
                    icon={Calendar}
                    label={days !== null && days >= 0 ? 'Days Remaining' : 'Status'}
                    value={days !== null && days >= 0 ? `${days} day${days !== 1 ? 's' : ''}` : 'Expired'}
                    valueClass={
                      days !== null && days < 0
                        ? 'text-red-600'
                        : days !== null && days <= 30
                        ? 'text-amber-700'
                        : ''
                    }
                  />
                </div>

                {(contract.contract_amount != null || contract.pay_now != null || contract.coupon_code) && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                    {contract.contract_amount != null && (
                      <InfoTile
                        icon={FileText}
                        label="Contract Value"
                        value={`${contract.contract_currency === 'USD' ? '$' : '₹'}${contract.contract_amount.toLocaleString('en-IN')} ${contract.contract_currency ?? 'INR'}`}
                      />
                    )}
                    <InfoTile
                      icon={CreditCard}
                      label="Billing Mode"
                      value={
                        contract.pay_now
                          ? 'Immediate'
                          : contract.trial_period_days && contract.trial_period_days > 0
                          ? `${contract.trial_period_days}-day trial`
                          : 'Deferred'
                      }
                    />
                    {contract.coupon_code && (
                      <InfoTile icon={Info} label="Discount Applied" value={contract.coupon_code} />
                    )}
                  </div>
                )}
              </Card>

              {/* ── Section 2 — Seat Usage ── */}
              <Card>
                <p className="text-sm font-semibold text-gray-900 mb-5">Seat Usage</p>
                <div className="flex flex-col gap-5">
                  <UsageBar
                    used={activeLearners}
                    total={contract.seat_count}
                    label="Learner Seats"
                    icon={Users}
                  />
                  {isFullCreator && (contract.content_creator_seats ?? 0) > 0 && (
                    <UsageBar
                      used={activeCCs}
                      total={contract.content_creator_seats}
                      label="Content Creator Seats"
                      icon={UserCog}
                    />
                  )}
                </div>
              </Card>

             
              {/* ── Section 4 — Payment Details ── */}
              {(contract.payment_method_brand || contract.payment_billing_email) && (
                <Card>
                  <div className="flex items-center gap-2 mb-4">
                    <CreditCard size={16} className="text-gray-400" />
                    <p className="text-sm font-semibold text-gray-900">Payment Details</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <InfoTile
                      icon={CreditCard}
                      label="Card on File"
                      value={
                        contract.payment_method_brand && contract.payment_method_last4
                          ? `${contract.payment_method_brand.charAt(0).toUpperCase()}${contract.payment_method_brand.slice(1)} •••• ${contract.payment_method_last4}`
                          : '—'
                      }
                    />
                    <InfoTile
                      icon={Mail}
                      label="Billing Email"
                      value={contract.payment_billing_email ?? '—'}
                    />
                  </div>
                  <div className="flex items-start gap-1.5 mt-3">
                    <Info size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-400">
                      Payment details are managed via Stripe and updated by your keySkillset account manager.
                    </p>
                  </div>
                </Card>
              )}

           {paymentHistory.length > 0 && (
    <Card>
        <p className="text-sm font-semibold text-gray-900 mb-4">Payment History</p>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        {['DATE', 'EVENT TYPE', 'AMOUNT', 'STATUS'].map(h => (
                            <th
                                key={h}
                                className="px-4 py-2.5 text-left text-xs font-medium text-gray-400 tracking-wide"
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {paymentHistory.map(row => (
                        <tr key={row.id}>
                            <td className="px-4 py-3 text-xs text-gray-500">
                                {row.createdAt
                                    ? new Date(row.createdAt).toLocaleDateString('en-IN', {
                                        day: '2-digit', month: 'short', year: 'numeric',
                                      })
                                    : '—'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500">
                                {row.eventType ?? '—'}
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                {row.amount != null && row.currency
                                    ? `${row.currency === 'USD' ? '$' : '₹'}${Number(row.amount).toLocaleString('en-IN')}`
                                    : '—'}
                            </td>
                            <td className="px-4 py-3">
                                <PaymentStatusBadge status={row.status ?? 'pending'} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </Card>
)}

              {/* ── Section 6 — Contract Notes ── */}
              {contract.notes && (
                <Card>
                  <p className="text-sm font-semibold text-gray-900 mb-2">Contract Notes</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{contract.notes}</p>
                </Card>
              )}

              {/* ── Section 7 — Contact CTA ── */}
              <div className="bg-violet-50 border border-violet-200 rounded-xl px-6 py-5">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-violet-900">Need to make changes?</p>
                    <p className="text-xs text-violet-600 mt-0.5">
                      Contact your keySkillset account manager to modify or renew your contract.
                    </p>
                  </div>
                  <a
                    href="mailto:contact@keyskillset.com"
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors flex-shrink-0"
                  >
                    <Mail size={14} /> Contact Support
                  </a>
                </div>
                <div className="flex items-start gap-2 mt-4 bg-violet-100 border border-violet-200 rounded-lg px-3 py-2.5">
                  <Mail size={14} className="text-violet-600 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-violet-700">
                    We typically respond to all contract enquiries within{' '}
                    <strong>2 business days</strong>. You will receive a confirmation at your
                    registered email once your request is received.
                  </p>
                </div>
              </div>

            </>
          )}
        </div>
      </div>
    </Page>
  );
}

// ─── Payment Status Badge ─────────────────────────────────────────────────────

function PaymentStatusBadge({ status }) {
  const map = {
    paid:     'bg-green-50 text-green-700',
    failed:   'bg-rose-50 text-rose-700',
    refunded: 'bg-amber-50 text-amber-700',
    pending:  'bg-gray-100 text-gray-500',
  };
  const cls = map[status] || 'bg-gray-100 text-gray-500';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${cls}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}